/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Geometry } from 'geojson';

interface Properties {
  datetime: string | null;
  'proj:wkt2'?: string;
}

interface STACAsset {
  href: string;
  type: string;
  created: string;
}

interface STACExtent {
  temporal: {
    interval: string[][];
  };
}

export interface STACLink {
  href: string;
  rel: string;
}

export interface STACItem {
  type: 'Feature';
  stac_version: string;
  id: string;
  geometry: Geometry | null;
  bbox: number[];
  properties: Properties;
  links: STACLink[];
  assets: Record<string, STACAsset>;
}

export type STACColormapEntry = [number, number, number, number];

export interface STACRenderEntry {
  title?: string;
  assets: string[];
  rescale?: number[][];
  colormap?: Record<string, STACColormapEntry>;
}

export interface STACCollection {
  type: 'Collection';
  stac_version: string;
  id: string;
  description: string;
  license: string;
  extent: STACExtent;
  links: STACLink[];
  features: STACItem[];
  renders?: Record<string, STACRenderEntry>;
}

export const fetchStacCollection = async (
  stacBaseUrlWithCollection: string,
): Promise<STACCollection> => {
  return (await (
    await fetch(stacBaseUrlWithCollection)
  ).json()) as STACCollection;
};

export const fetchStacItem = async (stacItemUrl: string): Promise<STACItem> => {
  return (await (await fetch(stacItemUrl)).json()) as STACItem;
};

export const fetchStacItems = async (
  collectionUrl: string,
): Promise<STACItem[]> => {
  const collection = await fetchStacCollection(collectionUrl);
  return collection.features as STACItem[];
};

export interface STACCatalogCollection {
  id: string;
  title?: string;
  description?: string;
  links: STACLink[];
}

export interface STACCatalogRoot {
  type?: string;
  stac_version?: string;
  id?: string;
  title?: string;
  description?: string;
  links: STACLink[];
  conformsTo?: string[];
}

interface STACCollectionsResponse {
  collections: STACCatalogCollection[];
}

export const fetchStacCatalogRoot = async (
  url: string,
): Promise<STACCatalogRoot> => {
  const requestUrl = url.trim();
  const response = await fetch(requestUrl, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch STAC root (${response.status} ${response.statusText})`,
    );
  }

  return (await response.json()) as STACCatalogRoot;
};

export const fetchStacCatalogCollections = async (
  baseUrl: string,
): Promise<STACCatalogCollection[]> => {
  const normalizedUrl = baseUrl.trim().replace(/\/+$/, '');
  const response = await fetch(`${normalizedUrl}/collections`, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch STAC collections (${response.status} ${response.statusText})`,
    );
  }

  const data = (await response.json()) as STACCollectionsResponse;
  return data.collections ?? [];
};
